import clsx from "clsx";
import { ArrowUpRight, Info } from "lucide-react";

import { Tooltip } from "@/design-system/components/Tooltip/Tooltip";

export function StatCard({
  icon: Icon,
  label,
  value,
  subtext,
  trend,
  color = "blue",
  tooltip,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  subtext?: string;
  trend?: number;
  color?: "blue" | "green" | "purple" | "orange";
  tooltip?: React.ReactNode;
}) {
  const iconClasses = {
    blue: "bg-surface-default/80 text-link group-hover:bg-surface-default",
    green:
      "bg-surface-default/80 text-green-600 group-hover:bg-surface-default",
    purple:
      "bg-surface-default/80 text-purple-600 group-hover:bg-surface-default",
    orange:
      "bg-surface-default/80 text-orange-600 group-hover:bg-surface-default",
  };

  const gradientClasses = {
    blue: "from-blue-500/10 via-transparent to-purple-500/10",
    green: "from-green-500/10 via-transparent to-emerald-500/10",
    purple: "from-purple-500/10 via-transparent to-pink-500/10",
    orange: "from-orange-500/10 via-transparent to-amber-500/10",
  };

  const blobColors = {
    blue: {
      blob1: "bg-blue-400",
      blob2: "bg-purple-400",
      center: "bg-blue-400",
    },
    green: {
      blob1: "bg-green-400",
      blob2: "bg-emerald-400",
      center: "bg-green-400",
    },
    purple: {
      blob1: "bg-purple-400",
      blob2: "bg-pink-400",
      center: "bg-purple-400",
    },
    orange: {
      blob1: "bg-orange-400",
      blob2: "bg-amber-400",
      center: "bg-orange-400",
    },
  };

  const glowClasses = {
    blue: "hover:shadow-blue-200/50",
    green: "hover:shadow-green-200/50",
    purple: "hover:shadow-purple-200/50",
    orange: "hover:shadow-orange-200/50",
  };

  return (
    <div className={clsx("aurora-border transition-all duration-300")}>
      <div
        className={`aurora-border-inner bg-surface-default group relative flex flex-col gap-3 overflow-hidden bg-gradient-to-br p-6 shadow-lg transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl ${gradientClasses[color]} ${glowClasses[color]}`}
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div
            className={`animate-float absolute -top-4 -left-4 h-20 w-20 rounded-full ${blobColors[color].blob1} opacity-25 blur-xl transition-transform duration-700 group-hover:translate-x-2 group-hover:translate-y-2`}
          />
          <div
            className={`animate-float-delayed absolute -right-6 -bottom-6 h-24 w-24 rounded-full ${blobColors[color].blob2} opacity-25 blur-xl transition-transform duration-700 group-hover:-translate-x-2 group-hover:-translate-y-2`}
          />
          <div
            className={`animate-pulse-slow absolute top-1/2 left-1/2 h-16 w-16 -translate-x-1/2 -translate-y-1/2 rounded-full ${blobColors[color].center} opacity-25 blur-2xl transition-transform duration-700 group-hover:scale-110`}
          />
        </div>

        <div className="relative z-10 flex items-center justify-between">
          <div
            className={`animate-idle-float rounded-xl p-2.5 shadow-sm transition-all duration-300 group-hover:scale-110 group-hover:shadow-md ${iconClasses[color]}`}
          >
            <Icon
              size={22}
              className="transition-transform group-hover:rotate-12"
            />
          </div>
          {trend !== undefined && (
            <div className="bg-status-success-tint text-status-success flex items-center gap-1 rounded-full px-2.5 py-1 text-sm font-medium">
              <ArrowUpRight size={16} className="animate-bounce" />
              {trend}%
            </div>
          )}
        </div>
        <div className="relative z-10">
          <p className="text-textSubtle flex items-center gap-1 text-sm font-medium">
            {label}
            {tooltip && (
              <Tooltip
                content={tooltip}
                triggerProps={{ style: { cursor: "default" } }}
              >
                <Info size={14} className="text-iconMinimal" />
              </Tooltip>
            )}
          </p>
          <p className="from-textDefault via-textTertiary to-textDefault mt-1 bg-gradient-to-r bg-[length:200%_auto] bg-clip-text text-3xl font-bold text-transparent transition-transform duration-300 group-hover:scale-[1.02]">
            {value}
          </p>
          {subtext && <p className="text-textSubtle mt-2 text-xs">{subtext}</p>}
        </div>
      </div>
    </div>
  );
}
